import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Pill } from '../ui';
import './imports.css';

const TONES = { queued: 'neutral', running: 'accent', done: 'green', failed: 'danger', cancelled: 'amber' };

/**
 * A background job's state: a progress bar (n of total), its status, and —
 * while queued or running with no worker seen for 5 minutes — that it waits
 * for the background worker, so a missing crontab is visible, not silent.
 */
function JobProgress({ job, onCancel, cancelling = false, label }) {
  const { t } = useTranslation();
  // Screen readers hear the status when it changes (waiting, running, done…), not every count.
  const [announced, setAnnounced] = useState('');
  const last = useRef(null);
  const status = job ? job.status : null;
  useEffect(() => {
    if (status && status !== last.current) {
      if (last.current !== null) setAnnounced(t(`jobs.status.${status}`));
      last.current = status;
    }
  }, [status, t]);
  if (!job) return null;
  const total = job.total || 0;
  const percent = total > 0 ? Math.min(100, Math.round((job.progress / total) * 100)) : (job.status === 'done' ? 100 : 0);
  const active = job.status === 'queued' || job.status === 'running';
  return (
    <div className="job-progress">
      <div className="job-progress__head">
        <span className="job-progress__label">{label || t(`jobs.types.${job.type}`, { defaultValue: job.type })}</span>
        <Pill tone={TONES[job.status] || 'neutral'}>{t(`jobs.status.${job.status}`)}</Pill>
      </div>
      <div className="job-progress__bar" role="progressbar" aria-valuemin={0} aria-valuemax={total || 100}
           aria-valuenow={total ? job.progress : percent} aria-label={t('jobs.progressLabel')}>
        <span style={{ width: `${percent}%` }} />
      </div>
      <span className="visually-hidden" role="status">{announced}</span>
      <p className="job-progress__count">
        {total ? t('jobs.count', { done: job.progress, total }) : t('jobs.countUnknown', { done: job.progress })}
      </p>
      {job.waiting_for_worker && (
        <Alert tone="warning" title={t('jobs.noWorker.title')}>{t('jobs.noWorker.body')}</Alert>
      )}
      {job.status === 'failed' && job.error && <Alert tone="danger" title={t('jobs.failed')}>{job.error}</Alert>}
      {active && onCancel && (
        <Button variant="secondary" size="sm" onClick={onCancel} loading={cancelling}>{t('jobs.cancel')}</Button>
      )}
    </div>
  );
}

export default JobProgress;
